/**
 * SegmentCreateAIPage — `/segments/new/ai`
 *
 * Layout estilo ChatGPT: chat protagónico al centro con sidebar derecha de
 * "estado vivo" (count + filtros + save). Cuando el chat está vacío, ocupa
 * todo el ancho con quick prompts grandes. Cuando hay conversación, aparece
 * la sidebar.
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
    ArrowLeft,
    Sparkles,
    Users,
    Save,
    RotateCcw,
    AlertCircle,
    Filter,
    Loader2,
    CheckCircle2,
    X,
} from "lucide-react";
import { Button } from "../../../shared/components/ui/Button";
import { FIELD_LABELS } from "../fieldLabels";
import { ConversationChat } from "../components/ConversationChat";
import { ClarificationModal } from "../components/ClarificationModal";
import { useSegmentConversation } from "../hooks/useSegmentConversation";

const MOCK_BRAND_ID = "brand-demo";

export function SegmentCreateAIPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [segmentName, setSegmentName] = useState("");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saved, setSaved] = useState<{ id: string } | null>(null);

    const {
        state,
        loading,
        error,
        needsClarification,
        clarificationQuestion,
        clarificationOptions,
        sendMessage,
        selectClarification,
        undo,
        removeFilter,
        reset,
        saveSegment,
        loadSegment,
    } = useSegmentConversation(MOCK_BRAND_ID);

    // Mount: o refinar un segmento existente (desde su detalle) o auto-enviar
    // un mensaje inicial (desde AudiencePage con un searchQuery).
    useEffect(() => {
        const st = location.state as
            | { initialMessage?: string; refineSegmentId?: string }
            | null;
        const noHistory = (state.history?.length ?? 0) === 0;
        if (st?.refineSegmentId && noHistory) {
            // Audit V4-14: "Refinar con IA" continúa la conversación sobre el segmento.
            loadSegment(st.refineSegmentId, MOCK_BRAND_ID);
        } else if (st?.initialMessage && noHistory) {
            sendMessage(st.initialMessage);
        }
        // Solo en mount — no depender de sendMessage/state para evitar loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canUndo =
        (state.history?.length ?? 0) > 0 &&
        state.undoIndex < (state.history?.length ?? 0);
    const hasFilters = Object.keys(state.filter || {}).length > 0;
    // Audit V4-14: también mostramos el sidebar cuando se cargó un segmento
    // para refinar (tiene filtros aunque todavía no haya mensajes).
    const hasConversation = (state.history?.length ?? 0) > 0 || hasFilters;

    const handleSave = async () => {
        if (!segmentName.trim()) {
            setSaveError("El nombre es requerido");
            return;
        }
        const result = await saveSegment(segmentName.trim());
        if (result) {
            setSaved({ id: result.id });
            setTimeout(() => {
                navigate(`/segments/${result.id}`);
            }, 800);
        } else {
            setSaveError(error || "Error al guardar");
        }
    };

    const handleSelectSuggestion = (suggestion: {
        label: string;
        promptToSend: string;
    }) => {
        sendMessage(suggestion.promptToSend);
    };

    return (
        <div className="min-h-screen p-6 md:p-8 animate-fade-in">
            <div className="mx-auto max-w-6xl space-y-5">
                {/* Breadcrumb */}
                <Link
                    to="/segments/new"
                    className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors"
                >
                    <ArrowLeft size={14} />
                    Cambiar de método
                </Link>

                {/* Header compacto */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 inline-flex items-center gap-1">
                                <Sparkles size={9} />
                                Con IA
                            </span>
                            {state.segmentId && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold uppercase tracking-wider">
                                    Borrador
                                </span>
                            )}
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                            Conversá con la IA
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">
                            Describí tu audiencia en una frase y la IA te arma el
                            filtro. Refiná con más mensajes hasta que quede como
                            querés.
                        </p>
                    </div>
                    {hasConversation && (
                        <div className="flex gap-2 flex-wrap">
                            <Button
                                variant="secondary"
                                onClick={reset}
                                disabled={loading}
                            >
                                <RotateCcw size={14} />
                                Empezar de nuevo
                            </Button>
                            {hasFilters && (
                                <Button
                                    variant="primary"
                                    onClick={() => setShowSaveModal(true)}
                                    disabled={loading}
                                >
                                    <Save size={14} />
                                    Guardar segmento
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {/* Error banner */}
                {error && (
                    <div className="flex items-center gap-3 p-3.5 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
                        <AlertCircle size={18} />
                        {error}
                    </div>
                )}

                {/* Layout dinámico:
                    - Sin conversación: chat ocupa todo el ancho
                    - Con conversación: chat (2/3) + sidebar live (1/3) */}
                <div
                    className={`grid gap-5 ${
                        hasConversation
                            ? "grid-cols-1 lg:grid-cols-3"
                            : "grid-cols-1"
                    }`}
                >
                    {/* Chat — siempre */}
                    <div className={hasConversation ? "lg:col-span-2" : ""}>
                        <ConversationChat
                            history={state.history || []}
                            suggestions={state.suggestions || []}
                            loading={loading}
                            canUndo={canUndo}
                            onSendMessage={sendMessage}
                            onSelectSuggestion={handleSelectSuggestion}
                            onUndo={undo}
                        />
                    </div>

                    {/* Sidebar live — solo si hay conversación */}
                    {hasConversation && (
                        <div className="space-y-4">
                            {/* Big count */}
                            <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl border-2 border-violet-200 p-5 sticky top-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Users
                                        size={14}
                                        className="text-violet-700"
                                    />
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700">
                                        Audiencia actual
                                    </p>
                                </div>
                                <p className="text-5xl font-black text-violet-700 tabular-nums leading-none">
                                    {(state.count || 0).toLocaleString("es-AR")}
                                </p>
                                <p className="text-sm text-violet-700/80 mt-2">
                                    usuarios coinciden
                                </p>

                                {/* Summary */}
                                {state.summary && (
                                    <div className="mt-4 pt-4 border-t border-violet-200/50">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-1.5">
                                            Resumen
                                        </p>
                                        <p className="text-xs text-slate-700 leading-relaxed italic">
                                            "{state.summary}"
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Filters detectados */}
                            {hasFilters && (
                                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <Filter
                                                size={13}
                                                className="text-emerald-600"
                                            />
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
                                                Filtros aplicados
                                            </p>
                                        </div>
                                        <span className="text-[10px] text-slate-400">
                                            Click ✕ para quitar
                                        </span>
                                    </div>
                                    <div className="space-y-1.5">
                                        {Object.entries(state.filter).map(
                                            ([key, val]) => (
                                                <FilterChip
                                                    key={key}
                                                    field={key}
                                                    value={val}
                                                    onRemove={() =>
                                                        removeFilter(key)
                                                    }
                                                    disabled={loading}
                                                />
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Save CTA */}
                            {hasFilters && (
                                <button
                                    onClick={() => setShowSaveModal(true)}
                                    disabled={loading}
                                    className="w-full group/save p-4 bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-2xl shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:shadow-emerald-500/30 transition-all active:scale-[0.98]"
                                >
                                    <div className="flex items-center justify-center gap-2 font-semibold text-sm">
                                        <Save size={15} />
                                        Guardar segmento
                                    </div>
                                    <p className="text-[11px] text-white/80 mt-1">
                                        Para reutilizarlo en campañas
                                    </p>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Clarification Modal */}
            {needsClarification && clarificationQuestion && (
                <ClarificationModal
                    question={clarificationQuestion}
                    options={clarificationOptions}
                    onSelect={selectClarification}
                    onClose={() => {}}
                />
            )}

            {/* Save Modal */}
            {showSaveModal && (
                <div
                    className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in p-4"
                    onClick={() => !saved && setShowSaveModal(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!saved ? (
                            <>
                                <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                                        <Save size={15} />
                                    </div>
                                    <h3 className="font-semibold text-lg text-slate-900">
                                        Guardar segmento
                                    </h3>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-0.5">
                                            Audiencia
                                        </p>
                                        <p className="text-2xl font-bold text-violet-700 tabular-nums">
                                            {(state.count || 0).toLocaleString(
                                                "es-AR",
                                            )}{" "}
                                            <span className="text-sm font-normal text-violet-700/70">
                                                usuarios
                                            </span>
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-900 mb-1.5">
                                            Nombre del segmento
                                        </label>
                                        <input
                                            type="text"
                                            value={segmentName}
                                            onChange={(e) =>
                                                setSegmentName(e.target.value)
                                            }
                                            placeholder="Ej. VIP Belgrano 30d+"
                                            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                                            autoFocus
                                        />
                                        {saveError && (
                                            <p className="text-red-600 text-xs mt-2 flex items-center gap-1.5">
                                                <AlertCircle size={12} />
                                                {saveError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
                                    <Button
                                        variant="secondary"
                                        onClick={() => {
                                            setShowSaveModal(false);
                                            setSaveError(null);
                                        }}
                                        className="flex-1"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSave}
                                        disabled={
                                            loading || !segmentName.trim()
                                        }
                                        className="flex-1"
                                    >
                                        {loading ? (
                                            <Loader2
                                                size={14}
                                                className="animate-spin"
                                            />
                                        ) : (
                                            <Save size={14} />
                                        )}
                                        Guardar
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="p-8 text-center">
                                <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
                                    <CheckCircle2
                                        size={32}
                                        className="text-emerald-600"
                                    />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-1">
                                    ¡Segmento guardado!
                                </h3>
                                <p className="text-sm text-slate-500">
                                    Abriendo el segmento…
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Sub: chip de un filtro detectado con botón X para quitar ─── */
function FilterChip({
    field,
    value,
    onRemove,
    disabled,
}: {
    field: string;
    value: unknown;
    onRemove: () => void;
    disabled: boolean;
}) {
    const fieldLabel = FIELD_LABELS[field] || field;
    const valueStr = formatFilterValue(value);
    return (
        <div className="group/chip flex items-center gap-2 px-2.5 py-2 bg-slate-50 hover:bg-violet-50/60 hover:border-violet-200 rounded-lg border border-slate-100 text-xs transition-colors">
            <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="font-mono text-slate-700 truncate">
                    {fieldLabel}
                </span>
                <span className="font-semibold text-violet-700 tabular-nums flex-shrink-0">
                    {valueStr}
                </span>
            </div>
            <button
                onClick={onRemove}
                disabled={disabled}
                className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                aria-label={`Quitar filtro de ${fieldLabel}`}
                title={`Quitar filtro de ${fieldLabel}`}
            >
                <X size={13} />
            </button>
        </div>
    );
}

// FIELD_LABELS se importa desde ../fieldLabels (compartido con el detalle de segmento).

function formatFilterValue(value: unknown): string {
    if (typeof value === "string" || typeof value === "number") {
        return String(value);
    }
    if (value && typeof value === "object") {
        const v = value as Record<string, any>;
        if (v.$gte !== undefined) return `≥ ${v.$gte}`;
        if (v.$gt !== undefined) return `> ${v.$gt}`;
        if (v.$lte !== undefined) return `≤ ${v.$lte}`;
        if (v.$lt !== undefined) return `< ${v.$lt}`;
        if (v.$eq !== undefined) return `= ${v.$eq}`;
        if (v.$ne !== undefined) return `≠ ${v.$ne}`;
        if (v.$in) return `en (${v.$in.length})`;
    }
    return String(value);
}

export default SegmentCreateAIPage;
